package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.model.InventoryItem
import fr.manyverse.app.domain.model.InventoryItemToCreate
import fr.manyverse.app.domain.repository.InventoryRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import fr.manyverse.app.infrastructure.realtime.GameChange
import fr.manyverse.app.infrastructure.realtime.GameChannelEvent
import fr.manyverse.app.infrastructure.realtime.GameRealtimeChannel
import fr.manyverse.app.infrastructure.serialization.appJson
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.channelFlow
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put

private const val TAG = "InventoryRepository"
private const val INVENTORY_TABLE = "character_inventory"

class SupabaseInventoryRepository(
    private val supabaseClient: SupabaseClient,
    private val gameChannel: GameRealtimeChannel
) : InventoryRepository {

    private val json = appJson { ignoreUnknownKeys = true }

    override suspend fun getItemsForCharacter(characterId: String): Result<List<InventoryItem>> {
        return try {
            val items = supabaseClient
                .from(INVENTORY_TABLE)
                .select(Columns.ALL) {
                    filter {
                        eq("character_id", characterId)
                    }
                }
                .decodeList<InventoryItem>()

            Result.success(items)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun createItem(item: InventoryItemToCreate): Result<InventoryItem> {
        return try {
            val insertDto = InventoryItemInsertDto(
                characterId = item.characterId,
                name = item.name,
                category = item.category.name,
                quantity = item.quantity,
                imageUrl = item.imageUrl
            )

            val createdItem = supabaseClient
                .from(INVENTORY_TABLE)
                .insert(insertDto) {
                    select()
                }
                .decodeSingle<InventoryItem>()

            Result.success(createdItem)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun deleteItem(itemId: String): Result<Unit> {
        return try {
            supabaseClient
                .from(INVENTORY_TABLE)
                .delete {
                    filter {
                        eq("id", itemId)
                    }
                }
            Result.success(Unit)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun updateQuantity(itemId: String, newQuantity: Int): Result<InventoryItem> {
        return try {
            val updatedItem = supabaseClient
                .from(INVENTORY_TABLE)
                .update(mapOf("quantity" to newQuantity)) {
                    filter {
                        eq("id", itemId)
                    }
                    select()
                }
                .decodeSingle<InventoryItem>()

            Result.success(updatedItem)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun updateImageUrl(itemId: String, imageUrl: String?): Result<InventoryItem> {
        return try {
            val updatedItem = supabaseClient
                .from(INVENTORY_TABLE)
                .update(mapOf("image_url" to imageUrl)) {
                    filter {
                        eq("id", itemId)
                    }
                    select()
                }
                .decodeSingle<InventoryItem>()

            Result.success(updatedItem)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override fun observeItemsForCharacter(characterId: String): Flow<List<InventoryItem>> = channelFlow {
        val items = mutableMapOf<String, InventoryItem>()
        var loaded = false

        gameChannel.events(INVENTORY_TABLE).collect { event ->
            when (event) {
                // Chargé à l'ouverture, puis rechargé après une coupure : les changements manqués ne
                // sont pas rejoués. Un échec du chargement initial termine le flux en erreur plutôt
                // que d'afficher un inventaire vide (déjà remonté par le mapper) ; après une coupure,
                // l'inventaire déjà connu reste affiché
                GameChannelEvent.Ready -> {
                    val reloaded = getItemsForCharacter(characterId)
                        .getOrElse { if (loaded) return@collect else throw it }
                    items.clear()
                    reloaded.forEach { items[it.id] = it }
                    loaded = true
                    send(items.values.toList())
                }

                is GameChannelEvent.Change -> {
                    val change = event.change
                    if (extractString(change.row?.get("character_id")) != characterId) return@collect
                    when (change.type) {
                        GameChange.Type.DELETE -> {
                            val id = extractString(change.oldRecord?.get("id")) ?: return@collect
                            if (items.remove(id) == null) return@collect
                        }

                        GameChange.Type.INSERT, GameChange.Type.UPDATE -> {
                            val item = change.record?.let(::decodeItem) ?: return@collect
                            items[item.id] = item
                        }
                    }
                    send(items.values.toList())
                }
            }
        }
    }

    private fun decodeItem(record: JsonObject): InventoryItem? = try {
        json.decodeFromJsonElement(InventoryItem.serializer(), record)
    } catch (e: Exception) {
        AppLogger.error(TAG, "Failed to parse inventory item from real-time update", e)
        null
    }

    override suspend fun transferItem(itemId: String, toCharacterId: String, quantity: Int): Result<Unit> {
        return try {
            supabaseClient.postgrest.rpc(
                "transfer_inventory_item",
                buildJsonObject {
                    put("p_from_item_id", itemId)
                    put("p_to_character_id", toCharacterId)
                    put("p_quantity", quantity)
                }
            )
            Result.success(Unit)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    @Serializable
    private data class InventoryItemInsertDto(
        @SerialName("character_id")
        val characterId: String,

        @SerialName("name")
        val name: String,

        @SerialName("category")
        val category: String,

        @SerialName("quantity")
        val quantity: Int,

        @SerialName("image_url")
        val imageUrl: String? = null
    )
}
